import { ChangeDetectionStrategy, Component, Input, OnInit, inject } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { OptimisationScheduleService } from '../optimisation-schedule.service'
import { OptimisationSchedule } from '../optimisation-schedule'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { WeekSchedule } from '../../schedules/work-schedule'
import { ElderToastService } from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { WeeklyScheduleEditComponent } from '../../schedules/weekly-schedule-edit/weekly-schedule-edit.component'
import { MatIcon } from '@angular/material/icon'
import { MatButton } from '@angular/material/button'
import { AsyncPipe } from '@angular/common'

@Component({
  selector: 'app-optimisation-schedule-detail',
  templateUrl: './optimisation-schedule-detail.component.html',
  styleUrls: ['./optimisation-schedule-detail.component.scss'],
  imports: [MatButton, MatIcon, WeeklyScheduleEditComponent, AsyncPipe, ElderTranslatePipe],
})
export class OptimisationScheduleDetailComponent implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)
  public readonly facilityId$ = new BehaviorSubject<string>(null)
  public readonly schedule$ = new BehaviorSubject<OptimisationSchedule>(null)
  private optimisationScheduleService = inject(OptimisationScheduleService)
  private toastService = inject(ElderToastService)

  @Input()
  public requestSave: Observable<Event>

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    this.requestSave.subscribe((_) => {
      const schedule = this.schedule$.getValue()
      if (schedule) {
        this.save(schedule)
      }
    })
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set facilityId(facilityId: string) {
    this.facilityId$.next(facilityId)
    this.schedule$.next(null)

    if (facilityId) {
      this.optimisationScheduleService.findById(facilityId).subscribe({
        next: (schedule) => this.schedule$.next(schedule),
        error: (_) => this.log.warn('No schedule found for facility: ' + facilityId),
      })
    }
  }

  public get facilityId(): string {
    return this.facilityId$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onNewSchedule(): void {
    if (this.facilityId) {
      const newSchedule = <OptimisationSchedule>{
        id: this.facilityId,
        weekSchedule: new WeekSchedule(),
      }
      this.save(newSchedule)
    } else {
      this.log.error('No facilityId set yet!')
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private save(schedule: OptimisationSchedule): void {
    this.optimisationScheduleService.update(schedule).subscribe({
      next: (updatedSchedule) => {
        this.schedule$.next(updatedSchedule)
        this.toastService.info('messages.persisting_successful')
      },
      error: (err) => this.toastService.error('messages.persisting_failed', err),
    })
  }
}
